'use client'

import React, { useMemo, useState, useEffect, useRef } from 'react'
import { usePathname } from 'next/navigation'
import { usePageInfo } from '@/shared/providers/context/PageContext'
import { useTheme } from '@/shared/theme'
import { Breadcrumb, createDefaultBreadcrumb } from './Breadcrumb'
import { EllipsisErrorBoundary } from '@/shared/components/error/EllipsisErrorBoundary'
import { useRouteNavigation } from '@/shared/hooks'
import { useAuth } from '@/domains/auth'
import { PageInfoPanel } from './PageInfoPanel'

function resolveHomeHref(
  isAuthenticated: boolean,
  user: unknown,
  pathname: string | null
): string {
  if (!isAuthenticated || !user) return '/'

  const u = user as { clientData?: unknown; roles?: string[] }
  const roles = Array.isArray(u.roles) ? u.roles.map((r) => String(r).toLowerCase()) : []
  const isClientRole = roles.includes('client')
  const hasClientData = Boolean(u.clientData)
  const onClientPath = Boolean(pathname?.startsWith('/client'))

  // Prefer in-memory user / route signals (SSR-safe). localStorage is only a
  // post-mount fallback when those are inconclusive.
  if (isClientRole || hasClientData || onClientPath) {
    return '/client/dashboard'
  }

  if (typeof window !== 'undefined') {
    try {
      if (localStorage.getItem('userType') === 'client') {
        return '/client/dashboard'
      }
    } catch {
      // ignore storage access errors
    }
  }

  return '/dashboard'
}

/**
 * SubNavbar — dense page chrome under the main Navbar.
 *
 * Layout (density 8-scale):
 *   [ breadcrumbs = location + page title ]  ·  optional short subtitle
 *   [ status chips + refresh / settings ]
 *
 * Description rules:
 *   - Only render when pageInfo.description is non-empty after trim.
 *   - usePageSetup does not inject a default — pages own narrative copy in body.
 *   - Avoid restating the last breadcrumb label as a second title.
 *
 * @accessibility
 * - Semantic nav + ARIA on breadcrumbs
 * - Keyboard-focusable actions in PageInfoPanel
 * - prefers-reduced-motion respected
 */
export default function SubNavbar({ children }: { children?: React.ReactNode }) {
  const { pageInfo } = usePageInfo()
  const { breadcrumbs: routeBreadcrumbs, title } = useRouteNavigation()
  const { token } = useTheme()
  const { isAuthenticated, user } = useAuth()
  const pathname = usePathname()

  // Client-only bits that would diverge from SSR if read during render
  const [mounted, setMounted] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [clientHomeHref, setClientHomeHref] = useState('/dashboard')
  const [reduceMotion, setReduceMotion] = useState(false)
  const navRef = useRef<HTMLDivElement>(null)
  const breadcrumbRef = useRef<HTMLDivElement>(null)

  const MOBILE_BREAKPOINT = 768
  const COLLAPSE_THRESHOLD = 900

  useEffect(() => {
    setMounted(true)

    setClientHomeHref(resolveHomeHref(isAuthenticated, user, pathname))

    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduceMotion(mq.matches)
    const onMotion = (e: MediaQueryListEvent) => setReduceMotion(e.matches)
    mq.addEventListener('change', onMotion)

    const handleResize = () => {
      const width = window.innerWidth
      setIsMobile(width < MOBILE_BREAKPOINT)
      setIsCollapsed(width < COLLAPSE_THRESHOLD)
    }
    handleResize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      mq.removeEventListener('change', onMotion)
    }
  }, [isAuthenticated, user, pathname])

  // SSR + first client paint: stable authenticated home → employee dashboard
  // (refined to client dashboard after mount when applicable). Never read
  // localStorage during render.
  const homeHref =
    mounted && isAuthenticated && user
      ? clientHomeHref
      : isAuthenticated && user
        ? '/dashboard'
        : '/'

  const breadcrumbs = useMemo(() => {
    const pageCrumbs = pageInfo.breadcrumbs
    const crumbs =
      Array.isArray(pageCrumbs) && pageCrumbs.length > 0
        ? pageCrumbs
        : Array.isArray(routeBreadcrumbs) && routeBreadcrumbs.length > 0
          ? routeBreadcrumbs
          : createDefaultBreadcrumb(pageInfo.title || title)

    return crumbs.map((crumb, index) => {
      const isHome =
        crumb.label === 'Home' ||
        crumb.href === '/' ||
        (index === 0 && (!crumb.href || crumb.href === '/'))

      if (isHome) {
        return { ...crumb, label: 'Home', href: homeHref }
      }
      return crumb
    })
  }, [pageInfo.breadcrumbs, routeBreadcrumbs, title, homeHref])

  // Until mounted, keep desktop layout for SSR/hydration parity (resize runs after)
  const mobile = mounted ? isMobile : false
  const collapsed = mounted ? isCollapsed : false

  const fadeTransition = reduceMotion
    ? 'none'
    : 'opacity 180ms ease, border-color 180ms ease, background-color 180ms ease'

  // Description only when explicitly set — never a second page title
  const lastCrumbLabel =
    breadcrumbs.length > 0
      ? String(breadcrumbs[breadcrumbs.length - 1]?.label ?? '').trim()
      : ''
  const rawDescription = pageInfo.description?.trim() || ''
  const descriptionText =
    rawDescription &&
    rawDescription.toLowerCase() !== lastCrumbLabel.toLowerCase() &&
    rawDescription.toLowerCase() !== String(pageInfo.title || '').trim().toLowerCase()
      ? rawDescription
      : null

  // Fine-tech subnav — same plane as navbar, hairline only (no parchment band)
  const subNavStyles = useMemo(() => {
    return {
      container: {
        position: 'relative' as const,
        width: '100%',
        background: 'var(--shell-subnav-bg, #FFFEFC)',
        borderBottom: '1px solid var(--shell-subnav-border, #d4c0ae)',
        boxShadow: 'none',
        zIndex: 100,
        overflow: 'hidden',
        transition: fadeTransition,
      },
      navContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: mobile ? '0 12px' : '0 16px',
        minHeight: 40,
        height: 40,
        maxWidth: '100%',
        margin: '0 auto',
        gap: 24,
      },
      navLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        flex: '1 1 auto',
        minWidth: 0,
        overflow: 'visible',
      },
      navRight: {
        display: 'flex',
        alignItems: 'center',
        flex: '0 0 auto',
        gap: 8,
      },
      breadcrumbContainer: {
        flex: '1 1 auto',
        minWidth: 0,
        overflow: 'visible',
        display: 'block',
        visibility: 'visible' as const,
        opacity: 1,
        height: 'auto',
      },
      descriptionInline: {
        marginLeft: 0,
        paddingLeft: 12,
        borderLeft: '1px solid var(--shell-subnav-border, #d4c0ae)',
        minWidth: 0,
        maxWidth: collapsed ? 160 : 240,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
        transition: fadeTransition,
      },
      descriptionMobile: {
        padding: '6px 12px 8px',
        background: 'var(--shell-subnav-bg, #FFFEFC)',
        fontSize: 12,
        lineHeight: 1.4,
        color: 'var(--color-text-secondary, ' + (token.colorTextSecondary || '#3A2A22') + ')',
        borderTop: '1px solid var(--shell-subnav-border, #d4c0ae)',
        transition: fadeTransition,
      },
      descriptionText: {
        fontSize: 12,
        lineHeight: 1.4,
        color: 'var(--color-text-secondary, ' + (token.colorTextSecondary || '#3A2A22') + ')',
        fontWeight: 400,
      },
      childrenSlot: {
        background: 'var(--shell-subnav-bg, #FFFEFC)',
        borderTop: '1px solid var(--shell-subnav-border, #d4c0ae)',
        padding: mobile ? '4px 12px' : '4px 20px',
        transition: fadeTransition,
      },
    }
  }, [token, mobile, collapsed, fadeTransition])

  return (
    <>
      <div style={subNavStyles.container} className="subnav-container">
        <div ref={navRef} style={subNavStyles.navContainer}>
          <div style={subNavStyles.navLeft}>
            <div ref={breadcrumbRef} style={subNavStyles.breadcrumbContainer}>
              <EllipsisErrorBoundary>
                <Breadcrumb items={breadcrumbs} />
              </EllipsisErrorBoundary>
            </div>

            {!mobile && descriptionText && (
              <div style={subNavStyles.descriptionInline} title={descriptionText}>
                <span style={subNavStyles.descriptionText}>{descriptionText}</span>
              </div>
            )}
          </div>

          <div style={subNavStyles.navRight}>
            <PageInfoPanel />
          </div>
        </div>

        {mobile && descriptionText && (
          <div style={subNavStyles.descriptionMobile}>{descriptionText}</div>
        )}
      </div>

      {children && <div style={subNavStyles.childrenSlot}>{children}</div>}
    </>
  )
}
